import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import {
  Sparkles,
  Palette,
  Image as ImageIcon,
  Upload as UploadIcon,
  MoreHorizontal,
  Grid3x3,
  Loader2,
  Check,
  RotateCcw,
  Music,
  Smile,
  Shapes,
  Trash2,
  X as XIcon,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';

import CoverPreview from '@/components/CoverPreview';
import {
  COLOR_PRESETS,
  GRADIENT_PRESETS,
  EMOJI_PICKS,
  EMOJI_BG_PRESETS,
} from '@/lib/coverPresets';
import { PATTERNS } from '@/lib/coverPatterns';
import api from '@/lib/api';

/**
 * CoverPickerModal — unified "Change cover" modal for all cover sources.
 *
 * Tabs:
 *   Default     → 2×2 track collage (resets any custom cover)
 *   AI          → Nano-Banana generation with optional vibe text
 *   Color       → 12 preset colors + custom hex input
 *   Gradient    → 8 curated gradients + custom builder (2 colors + angle)
 *   Upload      → JPEG/PNG/WebP with 1:1 crop + client compression
 *   More        → Top track art / Emoji cover / Pattern library
 *
 * Every cover change is optimistically pushed to the parent via
 * `onApplied(newCover)` and persisted server-side. The prior cover
 * gets saved to history so the "Recent" strip at the bottom offers
 * one-tap revert.
 */
export default function CoverPickerModal({
  open,
  onClose,
  bucket,
  initialCover,
  art = [],
  onApplied,
}) {
  const [draft, setDraft] = useState(() => normalizeCover(initialCover));
  const [tab, setTab] = useState(deriveInitialTab(initialCover));
  const [busy, setBusy] = useState(false);
  const [aiDescription, setAiDescription] = useState('');
  const [history, setHistory] = useState([]);
  const [historyBust, setHistoryBust] = useState(0);
  const [aiCoverKey, setAiCoverKey] = useState(0);

  // Reset draft/tab whenever the modal is re-opened for a (possibly)
  // different crate. This keeps the modal stateless between opens.
  useEffect(() => {
    if (open) {
      setDraft(normalizeCover(initialCover));
      setTab(deriveInitialTab(initialCover));
      setAiDescription('');
      // Load history on open (cheap; server caps at 5 entries).
      api
        .get(`/crates/${encodeURIComponent(bucket)}/cover/history`)
        .then(({ data }) => setHistory(data?.history || []))
        .catch(() => setHistory([]));
    }
  }, [open, bucket, initialCover]);

  const applyPatch = useCallback(
    async (payload) => {
      setBusy(true);
      try {
        const { data } = await api.patch(
          `/crates/${encodeURIComponent(bucket)}/cover`,
          payload,
        );
        toast.success('Cover updated');
        onApplied?.(data);
        setHistoryBust((n) => n + 1);
        onClose?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to update cover');
      } finally {
        setBusy(false);
      }
    },
    [bucket, onApplied, onClose],
  );

  const resetToDefault = useCallback(() => {
    applyPatch({ cover_type: 'default_collage', cover_config: {} });
  }, [applyPatch]);

  // -------- AI regenerate --------
  const handleAiGenerate = useCallback(async () => {
    setBusy(true);
    try {
      const body = aiDescription.trim() ? { description: aiDescription.trim() } : {};
      const { data } = await api.post(
        `/crates/${encodeURIComponent(bucket)}/cover`,
        body,
      );
      const newCover = normalizeCover(data);
      setDraft(newCover);
      setAiCoverKey((k) => k + 1);
      toast.success(initialCover?.cover_path ? 'Cover refreshed' : 'Cover generated');
      onApplied?.(data);
      setHistoryBust((n) => n + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cover generation failed');
    } finally {
      setBusy(false);
    }
  }, [bucket, aiDescription, onApplied, initialCover]);

  // -------- Top track quick action --------
  const handleTopTrackArt = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/crates/${encodeURIComponent(bucket)}/top-track-art`,
      );
      if (!data?.album_art_url) {
        toast.error('No album art available in this crate');
        return;
      }
      await applyPatch({
        cover_type: 'top_track',
        cover_config: {
          top_track_art_url: data.album_art_url,
          top_track_id: data.card_id,
          top_track_artist: data.artist,
          top_track_title: data.title,
        },
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No album art in this crate yet');
    } finally {
      setBusy(false);
    }
  }, [bucket, applyPatch]);

  // -------- Revert from history --------
  const handleRevertHistory = useCallback(
    async (entry) => {
      const type = entry?.cover_type;
      // For file-backed types we can't PATCH; those live in the DB with a
      // real cover_path so applying them requires a specialized route. For
      // now we support reverting to lightweight types via PATCH — the
      // most common flow (color → gradient → emoji → color again).
      const patchableTypes = new Set([
        'default_collage',
        'color',
        'gradient',
        'emoji',
        'top_track',
        'pattern',
      ]);
      if (!patchableTypes.has(type)) {
        toast.info('Reverting to that cover type isn\u2019t supported yet');
        return;
      }
      await applyPatch({
        cover_type: type,
        cover_config: entry.cover_config || {},
      });
    },
    [applyPatch],
  );

  const currentPreview = useMemo(
    () => ({
      bucket,
      art,
      coverType: draft.cover_type,
      coverConfig: draft.cover_config,
      coverPath: draft.cover_path,
      cacheKey: aiCoverKey,
    }),
    [bucket, art, draft, aiCoverKey],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose?.())}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="cover-picker-modal"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Palette className="w-4 h-4 text-[hsl(var(--primary))]" />
            Change cover — {bucket}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            Personalize your crate. Pick a template, style with a color or gradient,
            drop in your own image, or let AI cook one up.
          </DialogDescription>
        </DialogHeader>

        {/* Live preview + reset button */}
        <div className="px-4 pt-4 pb-3 flex gap-4 items-center border-b border-border/60">
          <div className="w-32 sm:w-40 shrink-0 rounded-xl overflow-hidden border border-border">
            <CoverPreview {...currentPreview} showAiBadge={false} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
              Live preview
            </div>
            <div className="text-sm font-medium text-foreground truncate">
              {draft.cover_type === 'default_collage'
                ? 'GroovLens default collage'
                : draft.cover_type === 'ai'
                ? 'AI-generated cover'
                : draft.cover_type === 'color'
                ? `Solid color · ${draft.cover_config?.color || ''}`
                : draft.cover_type === 'gradient'
                ? 'Gradient cover'
                : draft.cover_type === 'upload'
                ? 'Uploaded image'
                : draft.cover_type === 'top_track'
                ? 'Top track album art'
                : draft.cover_type === 'emoji'
                ? `Emoji · ${draft.cover_config?.emoji || ''}`
                : draft.cover_type === 'pattern'
                ? 'Pattern cover'
                : 'Custom cover'}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">
              Changes apply immediately when you tap Apply / Generate / Upload.
            </div>
            {draft.cover_type !== 'default_collage' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetToDefault}
                disabled={busy}
                className="mt-2 h-7 px-2 text-xs text-muted-foreground"
                data-testid="cover-reset-default-button"
              >
                <RotateCcw className="w-3 h-3 mr-1" />
                Reset to default
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList
              className="w-full justify-start rounded-none border-b border-border bg-transparent px-2 h-auto flex-wrap"
              data-testid="cover-picker-tabs"
            >
              <TabsTrigger value="default" data-testid="cover-tab-default">
                <Grid3x3 className="w-3.5 h-3.5 mr-1.5" /> Default
              </TabsTrigger>
              <TabsTrigger value="ai" data-testid="cover-tab-ai">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AI
              </TabsTrigger>
              <TabsTrigger value="color" data-testid="cover-tab-color">
                <Palette className="w-3.5 h-3.5 mr-1.5" /> Color
              </TabsTrigger>
              <TabsTrigger value="gradient" data-testid="cover-tab-gradient">
                <Palette className="w-3.5 h-3.5 mr-1.5" /> Gradient
              </TabsTrigger>
              <TabsTrigger value="upload" data-testid="cover-tab-upload">
                <UploadIcon className="w-3.5 h-3.5 mr-1.5" /> Upload
              </TabsTrigger>
              <TabsTrigger value="more" data-testid="cover-tab-more">
                <MoreHorizontal className="w-3.5 h-3.5 mr-1.5" /> More
              </TabsTrigger>
            </TabsList>

            {/* ---------- Default ---------- */}
            <TabsContent value="default" className="p-4 space-y-3 m-0">
              <p className="text-xs text-muted-foreground">
                The GroovLens default splits your crate into a 2×2 mosaic showing
                a live sample of the most recent album art in the crate. This is
                what every crate starts with and it auto-updates as you scan more.
              </p>
              <Button
                onClick={resetToDefault}
                disabled={busy || draft.cover_type === 'default_collage'}
                className="rounded-lg h-9"
                data-testid="cover-apply-default"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                )}
                Use default collage
              </Button>
            </TabsContent>

            {/* ---------- AI ---------- */}
            <TabsContent value="ai" className="p-4 space-y-3 m-0">
              <p className="text-xs text-muted-foreground">
                Generate a moody, abstract cover using AI. Every regeneration picks
                a fresh style seed so you never get the same painting twice.
              </p>
              <div>
                <Label
                  htmlFor="ai-vibe-input"
                  className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono"
                >
                  Vibe (optional)
                </Label>
                <Textarea
                  id="ai-vibe-input"
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value.slice(0, 140))}
                  placeholder="e.g. sunset ibiza rooftop — or leave blank for auto"
                  maxLength={140}
                  rows={2}
                  disabled={busy}
                  className="mt-1 text-xs resize-none"
                  data-testid="ai-vibe-input"
                />
                <div className="text-right text-[10px] font-mono text-muted-foreground tabular-nums mt-0.5">
                  {aiDescription.length}/140
                </div>
              </div>
              <Button
                onClick={handleAiGenerate}
                disabled={busy}
                className="rounded-lg h-9"
                data-testid="cover-ai-generate"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                )}
                {draft.cover_type === 'ai' && draft.cover_path
                  ? 'Regenerate cover'
                  : 'Generate cover'}
              </Button>
            </TabsContent>

            {/* ---------- Color ---------- */}
            <TabsContent value="color" className="p-4 space-y-4 m-0">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                  Presets
                </Label>
                <div className="mt-2 grid grid-cols-6 gap-2">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() =>
                        setDraft({
                          cover_type: 'color',
                          cover_config: { color: c.color },
                          cover_path: null,
                        })
                      }
                      title={c.label}
                      className={`h-10 rounded-lg border-2 transition-all ${
                        draft.cover_type === 'color' && draft.cover_config?.color === c.color
                          ? 'border-[hsl(var(--primary))] scale-[1.05]'
                          : 'border-border hover:border-foreground/40'
                      }`}
                      style={{ background: c.color }}
                      data-testid={`color-preset-${c.id}`}
                      aria-label={c.label}
                    />
                  ))}
                </div>
              </div>
              <div>
                <Label
                  htmlFor="custom-color-input"
                  className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono"
                >
                  Custom color
                </Label>
                <div className="mt-2 flex items-center gap-2">
                  <input
                    id="custom-color-input"
                    type="color"
                    value={draft.cover_config?.color || '#FF3EA5'}
                    onChange={(e) =>
                      setDraft({
                        cover_type: 'color',
                        cover_config: { color: e.target.value },
                        cover_path: null,
                      })
                    }
                    className="w-12 h-10 rounded-lg border border-border bg-background cursor-pointer"
                    data-testid="color-custom-picker"
                  />
                  <Input
                    type="text"
                    value={draft.cover_config?.color || ''}
                    onChange={(e) =>
                      setDraft({
                        cover_type: 'color',
                        cover_config: { color: e.target.value },
                        cover_path: null,
                      })
                    }
                    placeholder="#FF3EA5"
                    className="h-9 text-sm font-mono flex-1"
                    data-testid="color-custom-hex"
                  />
                </div>
              </div>
              <Button
                onClick={() =>
                  applyPatch({
                    cover_type: 'color',
                    cover_config: { color: draft.cover_config?.color || '#FF3EA5' },
                  })
                }
                disabled={busy || !draft.cover_config?.color}
                className="rounded-lg h-9"
                data-testid="cover-apply-color"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                )}
                Apply color
              </Button>
            </TabsContent>

            {/* ---------- Gradient ---------- */}
            <TabsContent value="gradient" className="p-4 space-y-4 m-0">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                  Curated gradients
                </Label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {GRADIENT_PRESETS.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() =>
                        setDraft({
                          cover_type: 'gradient',
                          cover_config: { from: g.from, to: g.to, angle: g.angle },
                          cover_path: null,
                        })
                      }
                      title={g.label}
                      className={`h-16 rounded-lg border-2 relative overflow-hidden transition-all ${
                        draft.cover_type === 'gradient' &&
                        draft.cover_config?.from === g.from &&
                        draft.cover_config?.to === g.to
                          ? 'border-[hsl(var(--primary))] scale-[1.03]'
                          : 'border-border hover:border-foreground/40'
                      }`}
                      style={{
                        background: `linear-gradient(${g.angle}deg, ${g.from}, ${g.to})`,
                      }}
                      data-testid={`gradient-preset-${g.id}`}
                      aria-label={g.label}
                    >
                      <span className="absolute bottom-1 left-1 right-1 text-[9px] font-medium text-white/95 text-left px-1 py-0.5 rounded bg-black/40 truncate">
                        {g.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-border p-3 space-y-3">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                  Custom gradient
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-muted-foreground mb-1">From</div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        value={draft.cover_config?.from || '#FF3EA5'}
                        onChange={(e) =>
                          setDraft({
                            cover_type: 'gradient',
                            cover_config: {
                              ...draft.cover_config,
                              from: e.target.value,
                              to: draft.cover_config?.to || '#18C7B8',
                              angle: draft.cover_config?.angle ?? 135,
                            },
                            cover_path: null,
                          })
                        }
                        className="w-10 h-9 rounded-lg border border-border bg-background cursor-pointer"
                        data-testid="gradient-from-picker"
                      />
                      <Input
                        value={draft.cover_config?.from || ''}
                        onChange={(e) =>
                          setDraft({
                            cover_type: 'gradient',
                            cover_config: {
                              ...draft.cover_config,
                              from: e.target.value,
                              to: draft.cover_config?.to || '#18C7B8',
                              angle: draft.cover_config?.angle ?? 135,
                            },
                            cover_path: null,
                          })
                        }
                        placeholder="#FF3EA5"
                        className="h-9 text-xs font-mono"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] text-muted-foreground mb-1">To</div>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        value={draft.cover_config?.to || '#18C7B8'}
                        onChange={(e) =>
                          setDraft({
                            cover_type: 'gradient',
                            cover_config: {
                              ...draft.cover_config,
                              from: draft.cover_config?.from || '#FF3EA5',
                              to: e.target.value,
                              angle: draft.cover_config?.angle ?? 135,
                            },
                            cover_path: null,
                          })
                        }
                        className="w-10 h-9 rounded-lg border border-border bg-background cursor-pointer"
                        data-testid="gradient-to-picker"
                      />
                      <Input
                        value={draft.cover_config?.to || ''}
                        onChange={(e) =>
                          setDraft({
                            cover_type: 'gradient',
                            cover_config: {
                              ...draft.cover_config,
                              from: draft.cover_config?.from || '#FF3EA5',
                              to: e.target.value,
                              angle: draft.cover_config?.angle ?? 135,
                            },
                            cover_path: null,
                          })
                        }
                        placeholder="#18C7B8"
                        className="h-9 text-xs font-mono"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
                    <span>Angle</span>
                    <span className="font-mono tabular-nums">
                      {draft.cover_config?.angle ?? 135}°
                    </span>
                  </div>
                  <Slider
                    min={0}
                    max={360}
                    step={5}
                    value={[draft.cover_config?.angle ?? 135]}
                    onValueChange={([v]) =>
                      setDraft({
                        cover_type: 'gradient',
                        cover_config: {
                          ...draft.cover_config,
                          from: draft.cover_config?.from || '#FF3EA5',
                          to: draft.cover_config?.to || '#18C7B8',
                          angle: v,
                        },
                        cover_path: null,
                      })
                    }
                    data-testid="gradient-angle-slider"
                  />
                </div>
              </div>
              <Button
                onClick={() =>
                  applyPatch({
                    cover_type: 'gradient',
                    cover_config: {
                      from: draft.cover_config?.from || '#FF3EA5',
                      to: draft.cover_config?.to || '#18C7B8',
                      angle: draft.cover_config?.angle ?? 135,
                    },
                  })
                }
                disabled={busy || !draft.cover_config?.from || !draft.cover_config?.to}
                className="rounded-lg h-9"
                data-testid="cover-apply-gradient"
              >
                {busy ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                )}
                Apply gradient
              </Button>
            </TabsContent>

            {/* ---------- Upload ---------- */}
            <TabsContent value="upload" className="p-4 m-0">
              <UploadTab
                bucket={bucket}
                busy={busy}
                setBusy={setBusy}
                onUploaded={(data) => {
                  setDraft(normalizeCover(data));
                  onApplied?.(data);
                  setHistoryBust((n) => n + 1);
                  onClose?.();
                }}
              />
            </TabsContent>

            {/* ---------- More ---------- */}
            <TabsContent value="more" className="p-4 space-y-5 m-0">
              {/* Top track quick action */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Music className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                  <div className="text-sm font-medium">Use top track album art</div>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  One tap — grabs the most recent track&apos;s album art from Spotify /
                  iTunes and uses it as the crate cover.
                </p>
                <Button
                  onClick={handleTopTrackArt}
                  disabled={busy}
                  variant="outline"
                  size="sm"
                  className="rounded-lg h-8"
                  data-testid="cover-more-top-track"
                >
                  <Music className="w-3.5 h-3.5 mr-1.5" />
                  Use top track art
                </Button>
              </div>

              {/* Emoji */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Smile className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                  <div className="text-sm font-medium">Emoji cover</div>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Big emoji + solid background. Pick or type your own.
                </p>
                <div className="grid grid-cols-8 gap-1.5 mb-2">
                  {EMOJI_PICKS.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() =>
                        setDraft({
                          cover_type: 'emoji',
                          cover_config: {
                            emoji: e,
                            emoji_bg: draft.cover_config?.emoji_bg || '#0F1116',
                          },
                          cover_path: null,
                        })
                      }
                      className={`h-9 rounded-md text-lg leading-none border transition-all ${
                        draft.cover_type === 'emoji' && draft.cover_config?.emoji === e
                          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                          : 'border-border hover:border-foreground/40'
                      }`}
                      data-testid={`emoji-pick-${e}`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <Input
                    value={draft.cover_type === 'emoji' ? draft.cover_config?.emoji || '' : ''}
                    onChange={(e) =>
                      setDraft({
                        cover_type: 'emoji',
                        cover_config: {
                          emoji: e.target.value.slice(0, 4),
                          emoji_bg: draft.cover_config?.emoji_bg || '#0F1116',
                        },
                        cover_path: null,
                      })
                    }
                    placeholder="or paste any emoji"
                    className="h-8 text-sm max-w-[160px]"
                    data-testid="emoji-custom-input"
                  />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground mb-1">Background</div>
                  <div className="flex flex-wrap gap-1.5">
                    {EMOJI_BG_PRESETS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() =>
                          setDraft({
                            cover_type: 'emoji',
                            cover_config: {
                              emoji: draft.cover_config?.emoji || '🎧',
                              emoji_bg: c,
                            },
                            cover_path: null,
                          })
                        }
                        className={`w-7 h-7 rounded-full border-2 ${
                          draft.cover_type === 'emoji' && draft.cover_config?.emoji_bg === c
                            ? 'border-[hsl(var(--primary))] scale-110'
                            : 'border-border'
                        }`}
                        style={{ background: c }}
                        data-testid={`emoji-bg-${c}`}
                        aria-label={`Emoji background ${c}`}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() =>
                    applyPatch({
                      cover_type: 'emoji',
                      cover_config: {
                        emoji: draft.cover_config?.emoji || '🎧',
                        emoji_bg: draft.cover_config?.emoji_bg || '#0F1116',
                      },
                    })
                  }
                  disabled={busy || draft.cover_type !== 'emoji' || !draft.cover_config?.emoji}
                  className="mt-3 rounded-lg h-8"
                  size="sm"
                  data-testid="cover-apply-emoji"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Apply emoji
                </Button>
              </div>

              {/* Pattern */}
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Shapes className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                  <div className="text-sm font-medium">Pattern library</div>
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Geometric SVG patterns tinted with your chosen color.
                </p>
                <div className="grid grid-cols-6 gap-2 mb-3">
                  {PATTERNS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        setDraft({
                          cover_type: 'pattern',
                          cover_config: {
                            pattern_id: p.id,
                            pattern_tint:
                              draft.cover_config?.pattern_tint || '#18C7B8',
                          },
                          cover_path: null,
                        })
                      }
                      className={`aspect-square rounded-md overflow-hidden border-2 ${
                        draft.cover_type === 'pattern' &&
                        draft.cover_config?.pattern_id === p.id
                          ? 'border-[hsl(var(--primary))] scale-[1.05]'
                          : 'border-border hover:border-foreground/40'
                      }`}
                      title={p.label}
                      data-testid={`pattern-pick-${p.id}`}
                      aria-label={p.label}
                    >
                      <div className="w-full h-full">
                        <CoverPreview
                          bucket={bucket}
                          coverType="pattern"
                          coverConfig={{
                            pattern_id: p.id,
                            pattern_tint:
                              draft.cover_config?.pattern_tint || '#18C7B8',
                          }}
                          showAiBadge={false}
                        />
                      </div>
                    </button>
                  ))}
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground mb-1">Tint</div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {COLOR_PRESETS.slice(0, 8).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() =>
                          setDraft({
                            cover_type: 'pattern',
                            cover_config: {
                              pattern_id:
                                draft.cover_config?.pattern_id || PATTERNS[0].id,
                              pattern_tint: c.color,
                            },
                            cover_path: null,
                          })
                        }
                        className={`w-7 h-7 rounded-full border-2 ${
                          draft.cover_type === 'pattern' &&
                          draft.cover_config?.pattern_tint === c.color
                            ? 'border-[hsl(var(--primary))] scale-110'
                            : 'border-border'
                        }`}
                        style={{ background: c.color }}
                        data-testid={`pattern-tint-${c.id}`}
                        aria-label={`Tint ${c.label}`}
                      />
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() =>
                    applyPatch({
                      cover_type: 'pattern',
                      cover_config: {
                        pattern_id: draft.cover_config?.pattern_id || PATTERNS[0].id,
                        pattern_tint: draft.cover_config?.pattern_tint || '#18C7B8',
                      },
                    })
                  }
                  disabled={busy || draft.cover_type !== 'pattern'}
                  className="mt-3 rounded-lg h-8"
                  size="sm"
                  data-testid="cover-apply-pattern"
                >
                  <Check className="w-3.5 h-3.5 mr-1.5" />
                  Apply pattern
                </Button>
              </div>
            </TabsContent>
          </Tabs>

          {/* Recent covers */}
          <HistoryStrip
            history={history}
            bucket={bucket}
            historyBust={historyBust}
            onRefresh={setHistory}
            onRevert={handleRevertHistory}
            disabled={busy}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Upload sub-tab (react-easy-crop + client-side compression)
// ---------------------------------------------------------------------------
function UploadTab({ bucket, busy, setBusy, onUploaded }) {
  const inputRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileName, setFileName] = useState('');

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPEG, PNG, or WebP images are supported');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is larger than 8MB');
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const clear = () => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setFileName('');
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      const form = new FormData();
      form.append('file', blob, 'cover.jpg');
      const { data } = await api.post(
        `/crates/${encodeURIComponent(bucket)}/cover/upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Cover uploaded');
      onUploaded?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  if (!imageSrc) {
    return (
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          className="w-full rounded-xl border-2 border-dashed border-border hover:border-[hsl(var(--primary))]/60 bg-background/40 p-8 text-center transition-colors"
          data-testid="upload-drop-zone"
        >
          <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm font-medium text-foreground">
            Drop an image here or tap to browse
          </div>
          <div className="text-[11px] text-muted-foreground mt-1">
            JPEG, PNG, WebP · up to 8MB · we&apos;ll crop it 1:1
          </div>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-black border border-border">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={1}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          objectFit="contain"
        />
      </div>
      <div>
        <div className="flex justify-between text-[10px] text-muted-foreground mb-1">
          <span>Zoom</span>
          <span className="font-mono tabular-nums">{zoom.toFixed(2)}×</span>
        </div>
        <Slider
          min={1}
          max={4}
          step={0.05}
          value={[zoom]}
          onValueChange={([v]) => setZoom(v)}
          data-testid="upload-zoom-slider"
        />
      </div>
      <div className="text-[11px] text-muted-foreground truncate" title={fileName}>
        {fileName}
      </div>
      <div className="flex gap-2">
        <Button
          onClick={handleUpload}
          disabled={busy || !croppedAreaPixels}
          className="rounded-lg h-9 flex-1"
          data-testid="upload-apply-button"
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <UploadIcon className="w-3.5 h-3.5 mr-1.5" />
          )}
          Upload &amp; use
        </Button>
        <Button
          variant="outline"
          onClick={clear}
          disabled={busy}
          className="rounded-lg h-9"
          data-testid="upload-clear-button"
        >
          <XIcon className="w-3.5 h-3.5 mr-1.5" />
          Clear
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History strip
// ---------------------------------------------------------------------------
function HistoryStrip({ history, bucket, historyBust, onRefresh, onRevert, disabled }) {
  useEffect(() => {
    if (historyBust > 0) {
      api
        .get(`/crates/${encodeURIComponent(bucket)}/cover/history`)
        .then(({ data }) => onRefresh(data?.history || []))
        .catch(() => {});
    }
  }, [historyBust, bucket, onRefresh]);

  if (!history || history.length === 0) return null;

  return (
    <div className="border-t border-border/60 px-4 py-3 bg-background/40">
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
        Recent covers
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {history.map((h, i) => (
          <button
            key={`hist-${i}`}
            type="button"
            onClick={() => onRevert(h)}
            disabled={disabled}
            className="w-14 h-14 shrink-0 rounded-md overflow-hidden border border-border hover:border-[hsl(var(--primary))] transition-colors"
            title={`Revert to ${h.cover_type}`}
            data-testid={`cover-history-${i}`}
          >
            <CoverPreview
              bucket={bucket}
              coverType={h.cover_type}
              coverConfig={h.cover_config}
              coverPath={h.cover_path}
              showAiBadge={false}
            />
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function normalizeCover(input) {
  if (!input) {
    return { cover_type: 'default_collage', cover_config: {}, cover_path: null };
  }
  const type = input.cover_type || (input.cover_path ? 'ai' : 'default_collage');
  return {
    cover_type: type,
    cover_config: input.cover_config || {},
    cover_path: input.cover_path || null,
  };
}

function deriveInitialTab(input) {
  const t = input?.cover_type;
  if (t === 'ai') return 'ai';
  if (t === 'color') return 'color';
  if (t === 'gradient') return 'gradient';
  if (t === 'upload') return 'upload';
  if (t === 'emoji' || t === 'top_track' || t === 'pattern') return 'more';
  return 'default';
}

/**
 * Client-side crop + downscale via Canvas so we never send >1200×1200
 * bytes to the server. Server still validates + re-processes to 1024,
 * but this keeps the upload wire time low on mobile.
 */
async function getCroppedBlob(imageSrc, area) {
  const img = await loadImage(imageSrc);
  const MAX = 1200;
  const scale = Math.min(1, MAX / Math.max(area.width, area.height));
  const outSize = Math.round(Math.max(area.width, area.height) * scale);
  const canvas = document.createElement('canvas');
  canvas.width = outSize;
  canvas.height = outSize;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    img,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    outSize,
    outSize,
  );
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
